import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Users, Trophy, Clock, ChevronRight, Medal, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import Navbar from '@/components/layout/Navbar';
import axios from 'axios';
import { API } from '@/App';
import { format, parseISO } from 'date-fns';

export default function LeagueDetailPage({ user }) {
  const { leagueId } = useParams();
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leagueRes, teamsRes, matchesRes, standingsRes] = await Promise.all([
          axios.get(`${API}/leagues/${leagueId}`),
          axios.get(`${API}/teams`, { params: { league_id: leagueId } }),
          axios.get(`${API}/matches`, { params: { league_id: leagueId } }),
          axios.get(`${API}/leagues/${leagueId}/standings`).catch(() => ({ data: { standings: [] } }))
        ]);
        setLeague(leagueRes.data);
        setTeams(teamsRes.data);
        setMatches(matchesRes.data);
        setStandings(standingsRes.data.standings || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [leagueId]);

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy • h:mm a');
    } catch {
      return dateStr;
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.team_id === teamId);
    return team?.name || 'Unknown Team';
  };

  const upcomingMatches = matches.filter(m => m.status === 'scheduled');
  const liveMatches = matches.filter(m => m.status === 'live');
  const completedMatches = matches.filter(m => m.status === 'completed');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} setUser={() => {}} />
        <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="skeleton h-12 w-64 rounded mb-4"></div>
            <div className="skeleton h-6 w-32 rounded mb-8"></div>
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="skeleton h-48 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} setUser={() => {}} />
        <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-2xl font-bold text-slate-900">League not found</h1>
            <Link to="/leagues" className="text-blue-600 hover:underline mt-4 inline-block">
              Back to Leagues
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} setUser={() => {}} />
      
      {/* Header */}
      <div className="pt-24 pb-8 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-7xl mx-auto">
          <Link to="/leagues" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Leagues
          </Link>
          
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {league.logo_url ? (
              <img src={league.logo_url} alt={league.name} className="w-24 h-24 rounded-2xl object-cover" />
            ) : (
              <div className="w-24 h-24 rounded-2xl team-logo-placeholder text-4xl">
                {league.name.charAt(0)}
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold text-white">{league.name}</h1>
              <Badge className="mt-2 bg-white/20 text-white">{league.season}</Badge>
              <div className="flex flex-wrap gap-4 mt-4 text-white/70 text-sm">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {league.location}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {formatDate(league.start_date)} - {formatDate(league.end_date)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-3 gap-4">
            <Card className="glass-card rounded-2xl">
              <CardContent className="p-4 text-center">
                <div className="font-stats text-2xl sm:text-3xl text-blue-600">{teams.length}</div>
                <div className="text-xs sm:text-sm text-slate-500">Teams</div>
              </CardContent>
            </Card>
            <Card className="glass-card rounded-2xl">
              <CardContent className="p-4 text-center">
                <div className="font-stats text-2xl sm:text-3xl text-blue-600">{matches.length}</div>
                <div className="text-xs sm:text-sm text-slate-500">Matches</div>
              </CardContent>
            </Card>
            <Card className="glass-card rounded-2xl">
              <CardContent className="p-4 text-center">
                <div className="font-stats text-2xl sm:text-3xl text-red-600">{liveMatches.length}</div>
                <div className="text-xs sm:text-sm text-slate-500">Live</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="standings" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6 rounded-xl bg-slate-100 p-1">
              <TabsTrigger value="standings" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <Medal className="w-4 h-4 mr-2" />
                Standings
              </TabsTrigger>
              <TabsTrigger value="stats" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <Activity className="w-4 h-4 mr-2" />
                Stats
              </TabsTrigger>
              <TabsTrigger value="teams" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <Users className="w-4 h-4 mr-2" />
                Teams
              </TabsTrigger>
              <TabsTrigger value="matches" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <Trophy className="w-4 h-4 mr-2" />
                Matches
              </TabsTrigger>
            </TabsList>

            {/* Standings Tab */}
            <TabsContent value="standings">
              {standings.length > 0 ? (
                <Card className="glass-card rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-12 text-center">#</TableHead>
                          <TableHead>Team</TableHead>
                          <TableHead className="text-center">P</TableHead>
                          <TableHead className="text-center">W</TableHead>
                          <TableHead className="text-center">L</TableHead>
                          <TableHead className="text-center">Pts</TableHead>
                          <TableHead className="text-center hidden sm:table-cell">NRR</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {standings.map((team, index) => (
                          <TableRow key={team.team_id} className={index < 4 ? 'bg-green-50/50' : ''}>
                            <TableCell className="text-center font-bold">
                              {team.position <= 3 ? (
                                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs ${
                                  team.position === 1 ? 'bg-yellow-500' :
                                  team.position === 2 ? 'bg-slate-400' :
                                  'bg-orange-600'
                                }`}>
                                  {team.position}
                                </span>
                              ) : team.position}
                            </TableCell>
                            <TableCell>
                              <Link to={`/teams/${team.team_id}`} className="flex items-center gap-2 hover:text-blue-600">
                                <div className="w-8 h-8 rounded-lg team-logo-placeholder text-xs">
                                  {team.short_name?.charAt(0) || team.team_name?.charAt(0)}
                                </div>
                                <span className="font-semibold">{team.short_name || team.team_name}</span>
                              </Link>
                            </TableCell>
                            <TableCell className="text-center">{team.played}</TableCell>
                            <TableCell className="text-center text-green-600 font-medium">{team.wins}</TableCell>
                            <TableCell className="text-center text-red-600 font-medium">{team.losses}</TableCell>
                            <TableCell className="text-center font-bold text-blue-600">{team.points}</TableCell>
                            <TableCell className="text-center hidden sm:table-cell">
                              <span className={`flex items-center justify-center gap-1 ${
                                team.net_run_rate >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {team.net_run_rate >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {team.net_run_rate >= 0 ? '+' : ''}{team.net_run_rate.toFixed(3)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="p-4 bg-slate-50 border-t text-xs text-slate-500">
                    <span className="inline-block w-3 h-3 bg-green-100 rounded mr-1"></span> Qualification zone
                  </div>
                </Card>
              ) : (
                <div className="empty-state glass-card rounded-2xl">
                  <div className="empty-state-icon">
                    <Medal className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">No standings yet</h3>
                  <p className="text-slate-500 mt-2">Standings will appear once matches are completed</p>
                </div>
              )}
            </TabsContent>

            {/* Stats Tab */}
            <TabsContent value="stats">
              <div className="text-center py-12">
                <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 rounded-2xl p-8 text-white max-w-2xl mx-auto">
                  <Activity className="w-16 h-16 mx-auto mb-4 text-orange-300" />
                  <h3 className="text-2xl font-bold mb-2">League Statistics & Leaderboards</h3>
                  <p className="text-white/80 mb-6">
                    View comprehensive player statistics, Orange Cap, Purple Cap, and all leaderboards for {league?.name}
                  </p>
                  <Link to={`/stats?league=${leagueId}`}>
                    <Button className="bg-white text-indigo-600 hover:bg-white/90 rounded-full px-8 font-semibold h-12" data-testid="view-league-stats-btn">
                      View Full Statistics <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="teams">
              {teams.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {teams.map((team) => (
                    <Link key={team.team_id} to={`/teams/${team.team_id}`}>
                      <Card className="glass-card glass-card-hover rounded-2xl cursor-pointer h-full">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-4">
                            {team.logo_url ? (
                              <img src={team.logo_url} alt={team.name} className="w-16 h-16 rounded-xl object-cover" />
                            ) : (
                              <div className="w-16 h-16 rounded-xl team-logo-placeholder text-2xl">
                                {team.short_name?.charAt(0) || team.name.charAt(0)}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-lg text-slate-900 truncate">{team.name}</h3>
                              {team.short_name && (
                                <p className="text-sm text-slate-500">{team.short_name}</p>
                              )}
                              {team.captain_name && (
                                <p className="text-sm text-slate-500 mt-1">Captain: {team.captain_name}</p>
                              )}
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state glass-card rounded-2xl">
                  <div className="empty-state-icon">
                    <Users className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">No teams yet</h3>
                  <p className="text-slate-500 mt-2">Teams will appear here once added</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="matches">
              {/* Live Matches */}
              {liveMatches.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="live-dot"></div>
                    <h3 className="text-lg font-semibold text-slate-900">Live Matches</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {liveMatches.map((match) => (
                      <Link key={match.match_id} to={`/matches/${match.match_id}`}>
                        <Card className="match-card glass-card glass-card-hover rounded-2xl cursor-pointer">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <Badge className="badge-live">LIVE</Badge>
                              <span className="text-sm text-slate-500">{match.overs} overs</span>
                            </div>
                            <div className="text-center py-2">
                              <p className="font-semibold">{getTeamName(match.team_a_id)}</p>
                              <p className="text-sm text-slate-400 my-1">vs</p>
                              <p className="font-semibold">{getTeamName(match.team_b_id)}</p>
                            </div>
                            <div className="text-sm text-slate-500 flex items-center gap-2 mt-3 pt-3 border-t">
                              <MapPin className="w-4 h-4" />
                              {match.ground}
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Upcoming Matches */}
              {upcomingMatches.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Upcoming Matches</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {upcomingMatches.map((match) => (
                      <Link key={match.match_id} to={`/matches/${match.match_id}`}>
                        <Card className="match-card glass-card glass-card-hover rounded-2xl cursor-pointer">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <Badge className="badge-scheduled">SCHEDULED</Badge>
                              <span className="text-sm text-slate-500">{match.overs} overs</span>
                            </div>
                            <div className="text-center py-2">
                              <p className="font-semibold">{getTeamName(match.team_a_id)}</p>
                              <p className="text-sm text-slate-400 my-1">vs</p>
                              <p className="font-semibold">{getTeamName(match.team_b_id)}</p>
                            </div>
                            <div className="space-y-1 mt-3 pt-3 border-t">
                              <div className="text-sm text-slate-500 flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                {formatDateTime(match.match_date_time)}
                              </div>
                              <div className="text-sm text-slate-500 flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                {match.ground}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Completed Matches */}
              {completedMatches.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Completed Matches</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {completedMatches.map((match) => (
                      <Link key={match.match_id} to={`/matches/${match.match_id}`}>
                        <Card className="match-card glass-card glass-card-hover rounded-2xl cursor-pointer">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <Badge className="badge-completed">COMPLETED</Badge>
                              <span className="text-sm text-slate-500">{match.overs} overs</span>
                            </div>
                            <div className="text-center py-2">
                              <p className="font-semibold">{getTeamName(match.team_a_id)}</p>
                              <p className="text-sm text-slate-400 my-1">vs</p>
                              <p className="font-semibold">{getTeamName(match.team_b_id)}</p>
                            </div>
                            <div className="text-sm text-slate-500 flex items-center gap-2 mt-3 pt-3 border-t">
                              <MapPin className="w-4 h-4" />
                              {match.ground}
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {matches.length === 0 && (
                <div className="empty-state glass-card rounded-2xl">
                  <div className="empty-state-icon">
                    <Trophy className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">No matches scheduled yet</h3>
                  <p className="text-slate-500 mt-2">Matches will appear here once scheduled</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
